<template>
	<div :class="['scrollbar__wrapper', wrapperClass]" :style="wrapperStyle">
		<div
			ref="wrapperEl"
			class="scrollbar__scroller"
			v-bind="$attrs"
			@wheel="handleScroll"
			@scroll="handleScroll"
		>
			<div
				ref="contentEl"
				:class="[
					'scrollbar__content',
					contentClass,
					{
						['scrollbar__content--fixedThumb']: fixedThumb,
						[`scrollbar__content--${direction}`]: direction,
					},
				]"
				:style="contentStyle"
			>
				<slot />
			</div>
		</div>

		<ScrollbarThumb
			v-for="(_, thumbType) in thumbs"
			:ref="instance => setThumbsInstance(instance, thumbType)"
			:key="thumbType"
			:autoExpand="autoExpand"
			:autoHide="autoHide"
			:autoHideDelay="autoHideDelay"
			:fixedThumb="thumbType === direction ? false : fixedThumb"
			:type="thumbType"
			:scrollInfo="
				thumbType === 'vertical' ? scrollHeightInfo : scrollWidthInfo
			"
			:thumbWidth="thumbWidth"
			:wrapperEl="wrapperEl"
		/>
	</div>
</template>

<script lang="ts">
export default {
	name: "CustomScrollbar",
	inheritAttrs: false,
};
</script>

<script lang="ts" setup>
import type Thumb from "@/components/ScrollbarThumb.vue";
import ScrollbarThumb from "@/components/ScrollbarThumb.vue";
import type { Rect } from "@/utils/useMeasure";
import useMeasure from "@/utils/useMeasure";
import { clamp } from "lodash-es";
import type { StyleValue } from "vue";
import { computed, onMounted, shallowRef, watch } from "vue";

interface Props {
	wrapperClass?: string;
	wrapperStyle?: StyleValue;
	contentClass?: string;
	contentStyle?: StyleValue;
	/** Set major scroll direction.Will auto set the scroll container element display to 'horizontal' -> 'inline-flex' / 'vertical' -> 'block'('inline-block' if fixedThumb). */
	direction?: "horizontal" | "vertical";
	thumbMinSize?: number;
	thumbMaxSize?: number;
	thumbWidth?: number;
	autoHide?: boolean;
	autoHideDelay?: number;
	autoExpand?: boolean;
	/** In fixedThumb mode, minor direction thumb will be fixed at edge of screen if scroller element out of window. */
	fixedThumb?: boolean;
	throttleType?: "throttle" | "debounce" | "none";
	throttleWait?: number;
	simulateScroll?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	direction: "vertical",
	autoHide: true,
	autoHideDelay: 900,
	autoExpand: true,
	throttleType: "debounce",
	throttleWait: 333,
	thumbMinSize: 48,
	thumbMaxSize: Infinity,
	thumbWidth: 12,
});

const emit = defineEmits<{
	(type: "wrapperResize", evt: Rect): void;
	(type: "contentResize", evt: Rect): void;
	(
		type: "scroll",
		evt: { target: Element; scrollLeft: number; scrollTop: number },
	): void;
}>();

/** Batch render Thumbs.Get the Thumb element and then set transform, autoHide onScroll. */
const thumbs = {
	horizontal: {
		el: null as unknown as HTMLElement,
		instance: null as unknown as typeof Thumb,
	},
	vertical: {
		el: null as unknown as HTMLElement,
		instance: null as unknown as typeof Thumb,
	},
};

const setThumbsInstance = (
	instance: any, // typeof Thumb
	direction: keyof typeof thumbs,
): void => {
	if (!thumbs[direction].instance) thumbs[direction].instance = instance;
};

let wrapperEl = shallowRef<HTMLDivElement>(null!);
let contentEl = shallowRef<HTMLDivElement>(null!);

onMounted((): void => {
	if (!wrapperEl?.value) return;

	const childNodes = Array.from(
		wrapperEl.value.parentElement?.childNodes ?? [],
	);
	for (const thumbType in thumbs) {
		thumbs[thumbType as keyof typeof thumbs].el = childNodes.find(ele =>
			(ele as HTMLElement)?.classList?.contains(
				`scrollbar__thumbPlaceholder--${thumbType}`,
			),
		) as HTMLElement;
	}
});

const updateMaxScrollDistance = (): void => {
	// Recalculate native scrollable distance should happen at both wrapperEl and contentEl rect changee.
	nativeMaxScrollTop.value = Math.max(
		(wrapperEl.value?.scrollHeight - wrapperRect.height) | 0,
		0,
	);
	nativeMaxScrollLeft.value = Math.max(
		(wrapperEl.value?.scrollWidth - wrapperRect.width) | 0,
		0,
	);
};

/** Measure the rect of wrapperEl and container el reactively, computed size of thumb. */
const wrapperRect = useMeasure(wrapperEl, {
	wait: props.throttleWait,
	type: props.throttleType,
	callback: updateMaxScrollDistance,
});

const contentRect = useMeasure(contentEl, {
	wait: props.throttleWait,
	type: props.throttleType,
	callback: updateMaxScrollDistance,
});

const nativeMaxScrollTop = shallowRef<number>(0);
const nativeMaxScrollLeft = shallowRef<number>(0);

interface ScrollInfo {
	thumbSize: number;
	contentMainSize: number;
	wrapperMainSize: number;
	boundaryDistance: number;
}

let scrollWidthInfo = computed((): ScrollInfo => {
	return {
		thumbSize: nativeMaxScrollLeft.value
			? clamp(
					(wrapperRect.width / wrapperEl.value.scrollWidth) *
						wrapperRect.width,
					props.thumbMinSize > wrapperRect.width
						? 48
						: props.thumbMinSize,
					props.thumbMaxSize,
				)
			: 0,
		/** used in mouse drag handler */
		contentMainSize: contentRect.width,
		wrapperMainSize: wrapperRect.width,
		/** for fixedThumb position */
		boundaryDistance: Math.abs(wrapperRect.left),
	};
});

let scrollHeightInfo = computed((): ScrollInfo => {
	return {
		thumbSize: nativeMaxScrollTop.value
			? clamp(
					(wrapperRect.height / wrapperEl.value.scrollHeight) *
						wrapperRect.height,
					props.thumbMinSize > wrapperRect.height
						? 48
						: props.thumbMinSize,
					props.thumbMaxSize,
				)
			: 0,
		contentMainSize: contentRect.height,
		wrapperMainSize: wrapperRect.height,
		boundaryDistance: Math.abs(wrapperRect.top),
	};
});

// Scrollable distance of simulate custom scrollbar's thumb.
let maxScrollTop = computed(
	(): number => wrapperRect.height - scrollHeightInfo.value.thumbSize - 5,
); // thumb has 3px's margin to wrapper;
let maxScrollLeft = computed(
	(): number => wrapperRect.width - scrollWidthInfo.value.thumbSize - 5,
);

const handleNativeScroll = (): void => {
	if (nativeMaxScrollLeft.value && wrapperEl?.value?.scrollLeft) {
		thumbs.horizontal.el.style.transform = `translate3d(${(wrapperEl.value.scrollLeft / nativeMaxScrollLeft.value) * maxScrollLeft.value}px, 0, 0)`;
		thumbs.horizontal.instance.autoHideAfterScroll();
	}
	if (nativeMaxScrollTop.value && wrapperEl?.value?.scrollTop) {
		thumbs.vertical.el.style.transform = `translate3d(0, ${(wrapperEl.value.scrollTop / nativeMaxScrollTop.value) * maxScrollTop.value}px, 0)`;
		thumbs.vertical.instance.autoHideAfterScroll();
	}
};

// should recalculate thumb position when dynamic content size change.
watch(
	(): [typeof nativeMaxScrollLeft, typeof nativeMaxScrollTop] => [
		nativeMaxScrollLeft,
		nativeMaxScrollTop,
	],
	handleNativeScroll,
);

const handleSimulateScroll = (evt: WheelEvent): void => {
	evt.stopPropagation();
	const preScrollLeft = wrapperEl.value.scrollLeft;
	const preScrollTop = wrapperEl.value.scrollTop;
	const newScrollLeft =
		clamp(
			preScrollLeft + (evt?.deltaX || 0),
			0,
			nativeMaxScrollLeft.value,
		) | 0;
	const newScrollTop =
		clamp(preScrollTop + (evt?.deltaY || 0), 0, nativeMaxScrollTop.value) |
		0;
	wrapperEl.value.scrollLeft = newScrollLeft;
	wrapperEl.value.scrollTop = newScrollTop;
	if (nativeMaxScrollLeft.value) {
		thumbs.horizontal.el.style.transform = `translate3d(${(newScrollLeft / nativeMaxScrollLeft.value) * maxScrollLeft.value}px, 0, 0)`;
		thumbs.horizontal.instance.autoHideAfterScroll();
	}
	if (nativeMaxScrollTop.value) {
		thumbs.vertical.el.style.transform = `translate3d(0, ${(newScrollTop / nativeMaxScrollTop.value) * maxScrollTop.value}px, 0)`;
		thumbs.vertical.instance.autoHideAfterScroll();
	}

	emit("scroll", {
		target: wrapperEl.value,
		scrollLeft: newScrollLeft,
		scrollTop: newScrollTop,
	});
};

const handleScroll = (evt: WheelEvent | UIEvent | Event): void => {
	if (props.simulateScroll) handleSimulateScroll(evt as WheelEvent);
	else handleNativeScroll();
};

watch(wrapperRect, (): void => emit("wrapperResize", wrapperRect));
watch(contentRect, (): void => emit("contentResize", contentRect));

defineExpose({
	scrollEl: wrapperEl,
});
</script>

<style lang="scss">
.scrollbar__wrapper {
	position: relative;
	height: fit-content;
	&:hover {
		> .scrollbar__thumbPlaceholder {
			opacity: 1;
		}
	}
}
.scrollbar__scroller {
	overflow: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;
	scroll-behavior: auto !important;
	&::-webkit-scrollbar {
		display: none;
	}
}

.scrollbar__content {
	&--vertical {
		display: block;
		&.scrollbar__content--fixedThumb {
			display: inline-block;
		}
	}
	&--horizontal {
		display: inline-flex;
	}
}
</style>
